import {
  Box,
  Button,
  Image,
  Link,
  Text,
  useToast,
} from '@chakra-ui/react';
import { BrokerType, brokerTypesData } from 'enums/shared/brokerType';
import { Form, Formik } from 'formik';
import useBrokerLogo from 'hooks/modules/brokerAccount/useBrokerLogo';
import useFormError from 'hooks/shared/useFormError';
import ConnectBrokerInstructionItem from 'presentation/components/modules/brokerAccount/create/ConnectBrokerInstructionItem';
import AppCard from 'presentation/components/shared/card/AppCard';
import FormTextInput from 'presentation/components/shared/form/FormTextInput';
import GridHeading from 'presentation/components/shared/grid/GridHeading';
import SettingsLayout from 'presentation/layout/modules/settings/SettingsLayout';
import { Color } from 'presentation/theme/colorScheme';
import { FC } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import AppRoute from 'router/AppRoute';
import { useCreateBrokerAccountMutation } from 'rtkQuery/sharespro/queries/brokerAccounts';
import * as Yup from 'yup';

interface FormValues {
  secret_key: string;
}

const ConnectTinkoffBrokerPage: FC = () => {
  const { t } = useTranslation();

  const brokerLogo = useBrokerLogo({ isFullLogo: true });

  const [createBroker, { isLoading }] = useCreateBrokerAccountMutation();

  const navigate = useNavigate();

  const formErrorHandler = useFormError<FormValues>();

  const validationSchema = Yup.object().shape({
    secret_key: Yup.string().required(t('fieldIsRequired') ?? ''),
  });

  const toast = useToast();

  return (
    <SettingsLayout>
      <Helmet>
        <title>{t('connectTinkoffBrokerTitle')}</title>
      </Helmet>
      <GridHeading>{t('connectTinkoffBrokerTitle')}</GridHeading>
      <AppCard p="40px" mb="20px" w="100%">
        <Image
          src={brokerLogo({ brokerType: BrokerType.Tinkoff })}
          w="200px"
          mb="20px"
          ml="120px"
        />
        <Formik<FormValues>
          initialValues={{
            secret_key: '',
          }}
          onSubmit={(values, formik) => {
            createBroker({
              broker_id: brokerTypesData[BrokerType.Tinkoff].id,
              secret_key: values.secret_key,
            })
              .unwrap()
              .then(() => {
                toast({
                  description: t('brokerAccountWasCreated'),
                  status: 'success',
                  position: 'top-right',
                  isClosable: true,
                });
                navigate(AppRoute.portfolio);
              })
              .catch((err) => formErrorHandler(err, formik));
          }}
          validationSchema={validationSchema}
        >
          {({ handleChange, values }) => {
            const steps = [
              <Text key={0}>
                {t('tinkoffBrokerConnectStep1_1')}
                <Link
                  href="https://www.tbank.ru/invest/settings/"
                  textDecoration="underline"
                  isExternal
                >
                  {t('tinkoffBrokerConnectStep1_2')}
                </Link>
              </Text>,
              <Text key={1}>{t('tinkoffBrokerConnectStep2')}</Text>,
              <Box key={2}>
                <Text mb="35px">{t('tinkoffBrokerConnectStep3')}</Text>
                <FormTextInput
                  onChange={handleChange}
                  value={values.secret_key}
                  name="secret_key"
                  placeholder={t('tinkoffCode') ?? ''}
                />
              </Box>,
            ];

            return (
              <Form>
                <Box position="relative" w="100%" mb="35px">
                  <Box w="100%">
                    {steps.map((stepText, index) => (
                      <Box key={index} mb="25px">
                        <ConnectBrokerInstructionItem
                          hasFormFields={index === 2}
                          step={index + 1}
                        >
                          {stepText}
                        </ConnectBrokerInstructionItem>
                      </Box>
                    ))}
                  </Box>
                  <Box
                    position="absolute"
                    left="78px"
                    top="0"
                    bgColor={Color.Shade100}
                    w="2px"
                    h="100%"
                  />
                </Box>
                <Button
                  ml="120px"
                  type="submit"
                  isLoading={isLoading}
                >
                  {t('continuePhrase')}
                </Button>
              </Form>
            );
          }}
        </Formik>
      </AppCard>
    </SettingsLayout>
  );
};

export default ConnectTinkoffBrokerPage;
